import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import { DropdownController } from "../../components/Core/Dropdown/Dropdown";
import TextArea from "../../components/Core/TextArea/TextArea";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { ContractCreateBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { ViewModels } from "../../configs/constants/ViewModels";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import Contract, {
	ContractWithoutCircularRef,
	CreateOrUpdateContractRequest,
} from "../../models/Contract";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useContract, useContracts, useCreateContract } from "../../networking/contract.hooks";
import { useAssignableInvoiceTemplates } from "../../networking/invoice-template.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { formatRoute } from "../../utils/route.utils";
import { ContractPriceController } from "./Prices/ContractPrices";
import { toast } from "../../utils/toast";

const inputsValidationSchema = yup.object().shape({
	name: yup.string().required("FIELD_IS_REQUIRED"),
	paymentDays: yup.number().typeError("VALUE_SHOULD_INTEGER"),
	invoiceTemplate: yup.object().shape({ id: yup.string().required("FIELD_IS_REQUIRED") }),
});

const ContractCreatePage: React.FC = () => {
	const { mutateAsync: createContract, isLoading: isCreating } = useCreateContract();
	const { data: organisation } = useOrganisation();
	const { contracts = [] } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		sort: CONTRACT_SORTING_COLUMN.NAME,
	});

	const [selectedFallbackContract, setSelectedFallbackContract] = useState(
		organisation?.defaultContractId
	);

	const { t } = useTranslation();

	const navigate = useNavigate();

	const { register, handleSubmit, control, formState, setValue, getValues } =
		useForm<ContractWithoutCircularRef>({
			mode: "onBlur",
			resolver: yupResolver(inputsValidationSchema),
			defaultValues: {
				fallbackContract: { id: organisation?.defaultContractId || "" },
				prices: [],
				paymentDays: 0,
				active: true,
				invoiceTemplate: { id: "" },
			},
		});

	// Invoice template is required; preselect the standard "Per uppdrag" (= the
	// rows every contract had before templates existed) once the list is in.
	const { templates: invoiceTemplates } = useAssignableInvoiceTemplates();
	useEffect(() => {
		if (getValues("invoiceTemplate.id")) return;
		const seeded = invoiceTemplates.find((t) => t.seeded === "DETAILED") ?? invoiceTemplates[0];
		if (seeded) setValue("invoiceTemplate.id", seeded.id);
	}, [invoiceTemplates, getValues, setValue]);

	const { contract: fallbackContract } = useContract({
		contractId: selectedFallbackContract || organisation?.defaultContractId,
		inheritFromFallback: true,
	});

	const onSubmit = handleSubmit(async (e) => {
		const updatedContract: CreateOrUpdateContractRequest = {
			name: e.name,
			paymentDays: e.paymentDays || 0,
			fallbackContract: e.fallbackContract?.id ? { id: e.fallbackContract?.id } : undefined,
			active: e.active,
			terms: e.terms,
			description: e.description,
			prices: e.prices,
			invoiceTemplate: { id: e.invoiceTemplate!.id },
		};

		const result = await createContract(updatedContract)
			.then(() => true)
			.catch(() => false);
		result ? navigateToContractList() : toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
	});

	const navigateToContractList = () => {
		navigate(routes.CONTRACTS);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={ContractCreateBreadcrumbs} />

			<Flex maxWidth="1200px" margin="2rem auto 4rem auto" direction="column">
				{isCreating ? (
					<LoadingIndicator />
				) : (
					<form onSubmit={onSubmit}>
						<Flex margin="2rem 0 0 0" direction="column">
							<AppInput
								{...register("name")}
								label="contract.name"
								description="contract.nameDesc"
								placeholder="NAME"
								errors={formState.errors?.name?.message}
								mandatory
							/>
							<AppInput
								{...register("paymentDays")}
								type="number"
								label="contract.invoiceDays"
								description="contract.invoiceDaysDesc"
								placeholder="CONTRACT_INVOICE_DAYS_PLACEHOLDER"
								errors={formState.errors?.paymentDays?.message}
							/>

							<TextArea
								{...register("terms")}
								label="contract.terms"
								description="contract.termsDesc"
								height="10rem"
								containerMargin="0 1rem 1rem 0"
							/>

							<TextArea
								{...register("description")}
								label="contract.privateDesc"
								description="contract.privateDescDesc"
								height="100px"
								containerMargin="0 1rem 1rem 0"
							/>

							{contracts.length > 0 && (
								<Flex alignItems="center">
									<DropdownController<Pick<Contract, "id" | "name">>
										label="contract.fallback"
										description="contract.fallbackDesc"
										placeholder="contract.select"
										control={control}
										name="fallbackContract.id"
										getOptionLabel={(option) => option.name}
										getValue={(val) => val.id}
										options={[{ id: "", name: t("NONE") }, ...contracts]}
										width="80%"
										onChange={setSelectedFallbackContract}
									/>
									{selectedFallbackContract && (
										<Link
											to={formatRoute(routes.CONTRACT_DETAILS + "/" + routes.CONTRACT_INFO, [
												selectedFallbackContract,
											])}
											target="_blank"
										>
											<AppText
												margin="0 0 0.5rem 0.5rem"
												textDecoration="underline"
												cursor="pointer"
											>
												{t("OPEN_IN_NEW_TAB")}
											</AppText>
										</Link>
									)}
								</Flex>
							)}
						</Flex>

						<ContractPriceController
							fallbackPrices={fallbackContract?.prices}
							control={control}
							name="prices"
							errors={formState.errors?.prices}
						/>

						<Flex margin="2rem 0 0 0" direction="column">
							<Checkbox
								{...register("active")}
								label={t("IS_ACTIVE")}
								description="contract.activeDesc"
							/>
						</Flex>

						<Flex margin="2rem 0 0 0" direction="column">
							<DropdownController<{ id: string; name: string }>
								label="contract.invoiceTemplate"
								description="contract.invoiceTemplateDesc"
								placeholder="contract.selectInvoiceTemplate"
								control={control}
								name="invoiceTemplate.id"
								getOptionLabel={(option) => option.name}
								getValue={(val) => val.id}
								options={invoiceTemplates.map((tpl) => ({ id: tpl.id, name: tpl.name }))}
								errors={formState.errors?.invoiceTemplate?.id?.message}
								width="80%"
								mandatory
							/>
						</Flex>
						<FloatingButtonPanel>
							<Button disabled={formState.isSubmitting} width="9.75rem">
								{t("SAVE_CHANGES")}
							</Button>
						</FloatingButtonPanel>
					</form>
				)}
			</Flex>
		</div>
	);
};

export default ContractCreatePage;
